import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Layout from '../components/Layout';
import { rhymesData } from '../data/rhymes';
import { useSpeech } from '../hooks/useSpeech';
import { ArrowLeft, ArrowRight, Play, Square } from 'lucide-react';
import './RhymeDetail.css';

const RhymeDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const { cancel } = useSpeech();
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isPlaying, setIsPlaying] = useState(false);
    const utteranceRef = useRef(null);

    const rhymeIndex = rhymesData.findIndex(r => r.id === id);
    const rhyme = rhymesData[rhymeIndex];

    useEffect(() => {
        return () => {
            cancel();
            setIsPlaying(false);
        };
    }, [id, cancel]);

    if (!rhyme) return <Layout title="Error">Rhyme not found</Layout>;

    const handleNext = () => {
        const nextIndex = (rhymeIndex + 1) % rhymesData.length;
        history.push(`/rhymes/${rhymesData[nextIndex].id}`);
    };

    const handlePrev = () => {
        const prevIndex = (rhymeIndex - 1 + rhymesData.length) % rhymesData.length;
        history.push(`/rhymes/${rhymesData[prevIndex].id}`);
    };

    const startReading = () => {
        if (!window.speechSynthesis) return;

        cancel();
        setIsPlaying(true);

        const fullText = rhyme.lyrics.join('. '); // Join with dots for better pauses
        const utterance = new SpeechSynthesisUtterance(fullText);

        // Rhythmic settings for kids
        utterance.rate = 0.8;
        utterance.pitch = 1.2;

        const voices = window.speechSynthesis.getVoices();
        const preferredVoice = voices.find(v => v.name.includes('Samantha') || v.name.includes('Google US English'));
        if (preferredVoice) utterance.voice = preferredVoice;

        // Cumulative char counts to track lines
        const lineOffsets = [];
        let cumulative = 0;
        rhyme.lyrics.forEach((line) => {
            lineOffsets.push({
                start: cumulative,
                end: cumulative + line.length + 2 // +2 for the ". " joiner
            });
            cumulative += line.length + 2;
        });

        utterance.onboundary = (event) => {
            if (event.name === 'word') {
                const charIndex = event.charIndex;
                const index = lineOffsets.findIndex(offset => charIndex >= offset.start && charIndex < offset.end);
                if (index !== -1) {
                    setActiveIndex(index);
                }
            }
        };

        utterance.onend = () => {
            setIsPlaying(false);
            setActiveIndex(-1);
        };

        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    };

    const stopReading = () => {
        cancel();
        setIsPlaying(false);
        setActiveIndex(-1);
    };

    // Render themed background elements
    const renderAnimations = () => {
        switch (rhyme.id) {
            case 'twinkle-twinkle':
                return [1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="floating-element star" style={{ left: `${i * 18}%`, top: `${(i % 3) * 15 + 10}%`, animationDelay: `${i * 0.5}s` }}>⭐</div>
                ));
            case 'itsy-bitsy-spider':
                return <div className="floating-element spider">🕷️</div>;
            case 'row-row-row-your-boat':
                return <div className="floating-element boat" style={{ top: '70%', left: '40%' }}>🚣</div>;
            case 'baa-baa-black-sheep':
                return <div className="floating-element" style={{ top: '15%', right: '10%', animation: 'float 5s infinite' }}>🐑</div>;
            case 'old-macdonald':
                return <div className="floating-element" style={{ top: '15%', left: '5%', animation: 'float 6s infinite' }}>🚜</div>;
            case 'hey-diddle-diddle':
                return <div className="floating-element" style={{ top: '10%', right: '20%', animation: 'float 4s infinite' }}>🌙</div>;
            default:
                return null;
        }
    };

    return (
        <Layout title={rhyme.title} onBack={() => history.push('/rhymes')}>
            <div key={id} className="rhyme-detail-container" style={{ backgroundColor: rhyme.color }}>
                {renderAnimations()}

                <div className="rhyme-card">
                    <div style={{ fontSize: '4rem', marginBottom: '10px' }}>{rhyme.emoji}</div>

                    <button
                        className="rhyme-action-button"
                        onClick={isPlaying ? stopReading : startReading}
                        style={{ margin: '0 auto 20px' }}
                    >
                        {isPlaying ? <><Square size={24} fill="currentColor" /> Stop</> : <><Play size={24} fill="currentColor" /> Sing Rhyme</>}
                    </button>

                    <div className="rhyme-lyrics">
                        {rhyme.lyrics.map((line, index) => (
                            <div
                                key={index}
                                className={`lyric-line ${index === activeIndex ? 'active' : ''}`}
                            >
                                {line}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="controls-container">
                    <button className="nav-arrow" onClick={handlePrev}><ArrowLeft size={32} /></button>
                    <button className="nav-arrow" onClick={handleNext}><ArrowRight size={32} /></button>
                </div>
            </div>
        </Layout>
    );
};

export default RhymeDetail;
